import React, { useState, useEffect } from 'react';
import { X, Shield, Cookie, Settings } from 'lucide-react';
import CookieSettings from './CookieSettings';

const CookieBanner = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    useEffect(() => {
        // Check if user has already accepted cookies
        const cookieConsent = localStorage.getItem('cookieConsent');
        if (!cookieConsent) {
            // Show banner after a short delay
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem('cookieConsent', 'accepted');
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        setIsVisible(false);
    };

    const handleDecline = () => {
        localStorage.setItem('cookieConsent', 'declined');
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        setIsVisible(false);
    };

    const handleClose = () => {
        setIsVisible(false);
    };

    const handleShowSettings = () => {
        setShowSettings(true);
    };

    const handleSettingsClose = () => {
        setShowSettings(false);
    };

    const handleSettingsSave = (settings) => {
        console.log('Cookie settings saved:', settings);
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 z-40" />

            {/* Banner */}
            <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-soft border border-secondary-200 overflow-hidden">
                        <div className="relative">
                            {/* Close button */}
                            <button
                                onClick={handleClose}
                                className="absolute top-4 right-4 p-2 text-secondary-500 hover:text-secondary-700 rounded-full hover:bg-secondary-50 transition-colors z-10"
                                aria-label="Закрыть"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <div className="p-6 sm:p-8">
                                <div className="flex items-start space-x-4">
                                    {/* Icon */}
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center">
                                            <Cookie className="w-6 h-6 text-white" />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg sm:text-xl font-semibold text-secondary-900 mb-3">
                                            Мы используем файлы cookie
                                        </h3>

                                        <p className="text-sm sm:text-base text-secondary-600 leading-relaxed mb-6">
                                            Для улучшения работы сайта и персонализации вашего опыта мы используем файлы cookie.
                                            Продолжая пользоваться сайтом, вы соглашаетесь с нашей{' '}
                                            <a
                                                href="/pages/politikakonfidenzialnosti"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary-600 hover:text-primary-700 font-medium underline"
                                            >
                                                Политикой конфиденциальности
                                            </a>
                                            {' '}и использованием cookie.
                                        </p>

                                        {/* Buttons */}
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <button
                                                onClick={handleAccept}
                                                className="flex-1 sm:flex-none px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm active:scale-[0.99]"
                                            >
                                                Принять все
                                            </button>

                                            <button
                                                onClick={handleDecline}
                                                className="flex-1 sm:flex-none px-6 py-3 bg-white text-secondary-700 font-medium rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors duration-200"
                                            >
                                                Только необходимые
                                            </button>

                                            <button
                                                onClick={handleShowSettings}
                                                className="flex items-center justify-center px-4 py-3 text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                                            >
                                                <Settings className="w-4 h-4 mr-2" />
                                                Настройки
                                            </button>

                                            <a
                                                href="/pages/politikakonfidenzialnosti"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-center px-4 py-3 text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                                            >
                                                <Shield className="w-4 h-4 mr-2" />
                                                Подробнее
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Decorative gradient */}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-primary-500" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Cookie Settings Modal */}
            <CookieSettings
                isOpen={showSettings}
                onClose={handleSettingsClose}
                onSave={handleSettingsSave}
            />
        </>
    );
};

export default CookieBanner;
